import { inject } from '@angular/core';
import { CanActivateChildFn, GuardResult, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { Jwt } from '../../utils/jwt.utils';
import { isSubscriptionExpired } from '../../utils/subscription.utils';

/**
 * Sends users whose tax office has no subscription access to the reactivation page.
 *
 * Three sources are consulted in order of freshness, and the first one that can answer decides — later
 * sources are not even fetched:
 *
 * 1. `subscriptionBlocked` from `GET /User/me` — evaluated against the database on every call, so it
 *    releases the lock immediately after a payment. Must win over the token claim, which can be up to one
 *    access-token lifetime (30 min in production) old; otherwise a user who just paid stays locked out.
 * 2. The `SubscriptionBlocked` token claim — read from the token already in memory, so unlike an API
 *    response it cannot silently fail open when the network or the API is down, and it costs no request.
 * 3. The subscription data — only fetched when the user could not be loaded at all and the claim says
 *    nothing, so the common path issues exactly one request instead of two.
 *
 * The backend enforces independently (`SubscriptionAccessFilter` plus the background jobs); this guard only
 * decides where to route.
 *
 * Registered as `canActivateChild` on `/main`: a parent's `canActivate` runs only when that parent is
 * activated, so navigating between children would otherwise never re-check and a user already inside the app
 * could keep clicking after their subscription lapsed.
 */
/**
 * How long a freshly fetched `/User/me` answer is reused before the guard asks again.
 *
 * Running as `canActivateChild` means this guard fires on every navigation between `/main` children, and each
 * fetch blocks rendering until the API answers. A short window keeps mid-session lapse detection — a lapse is
 * noticed within seconds — without putting a blocking request in front of every single click.
 */
const FRESHNESS_WINDOW_MS = 15_000;

let lastCheckedAt = 0;

export const subscriptionGuard: CanActivateChildFn = async (): Promise<GuardResult> => {
  const router = inject(Router);
  const userService = inject(UserService);
  const authService = inject(AuthService);

  const blocked = await isBlocked(userService, authService);

  return blocked ? router.createUrlTree(['/subscription-expired']) : true;
};

async function isBlocked(userService: UserService, authService: AuthService): Promise<boolean> {
  const now = Date.now();
  const isStale = now - lastCheckedAt > FRESHNESS_WINDOW_MS;

  // Also refetched when no user is cached at all — otherwise a logout inside the freshness window would leave
  // the guard falling through to the token claim instead of asking who is actually signed in now.
  if (isStale || !userService.currentUser) {
    await userService.loadCurrentUser();
    lastCheckedAt = now;
  }

  const currentUser = userService.currentUser;
  if (currentUser) {
    return currentUser.subscriptionBlocked;
  }

  // From here on the user could not be loaded, so fall back to the token and only then to the API.
  if (Jwt.isSubscriptionBlocked(authService.getAccessTokenSync())) {
    return true;
  }

  await userService.loadSubscriptionData(true);

  return isSubscriptionExpired(userService.getCurrentSubscription());
}
